import { useState, useMemo } from 'react';
import MetricCard from '../shared/MetricCard';
import DataTable from '../shared/DataTable';
import EmptyState from '../shared/EmptyState';
import { formatMoney, formatDate } from '../../utils/formatters';

export default function DuplicateOrders({ data }) {
  const [tab, setTab] = useState('same_ro');

  const sameRo = data?.same_ro_duplicates || [];
  const crossRo = data?.cross_ro_duplicates || [];
  const totalCost = data?.total_duplicate_cost_usd || 0;

  const sameRoColumns = useMemo(() => [
    {
      key: 'part_name',
      label: 'Part',
      render: (row) => (
        <div>
          <div className="font-bold text-sm">{row.part_name}</div>
          <div className="text-xs text-gray-400">{row.part_number}</div>
        </div>
      ),
    },
    {
      key: 'ro_number',
      label: 'RO #',
      render: (row) => <span className="font-semibold">#{row.ro_number}</span>,
    },
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (row) => <span className="text-sm">{row.vendor_name}</span>,
    },
    {
      key: 'order_count',
      label: 'Times Ordered',
      render: (row) => (
        <span className="px-2 py-0.5 bg-pink-50 text-pink-700 rounded-full text-xs font-bold">
          {row.order_count || row.duplicate_count || 2}x
        </span>
      ),
    },
    {
      key: 'combined_cost_usd',
      label: 'Combined Cost',
      align: 'right',
      render: (row) => <span className="font-bold">{formatMoney(row.combined_cost_usd)}</span>,
    },
  ], []);

  const crossRoColumns = useMemo(() => [
    {
      key: 'part_name',
      label: 'Part',
      render: (row) => (
        <div>
          <div className="font-bold text-sm">{row.part_name}</div>
          <div className="text-xs text-gray-400">{row.part_number}</div>
        </div>
      ),
    },
    {
      key: 'ro_numbers',
      label: 'ROs',
      render: (row) => <span className="text-xs">{row.ro_numbers || `${row.ro_number_1}, ${row.ro_number_2}`}</span>,
    },
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (row) => <span className="text-sm">{row.vendor_name}</span>,
    },
    {
      key: 'combined_cost_usd',
      label: 'Combined Cost',
      align: 'right',
      render: (row) => <span className="font-bold">{formatMoney(row.combined_cost_usd)}</span>,
    },
  ], []);

  const items = tab === 'same_ro' ? sameRo : crossRo;
  const columns = tab === 'same_ro' ? sameRoColumns : crossRoColumns;

  return (
    <div>
      <div className="flex gap-3 mb-6 flex-wrap">
        <MetricCard
          label="Same-RO Duplicates"
          value={sameRo.length.toString()}
          accent="#EC4899"
          icon={'\uD83D\uDCC4'}
        />
        <MetricCard
          label="Cross-RO Duplicates"
          value={crossRo.length.toString()}
          accent="#DB2777"
        />
        <MetricCard
          label="Total Duplicate Cost"
          value={formatMoney(totalCost)}
          sub="Combined cost of duplicated orders"
          accent="#C62828"
        />
      </div>

      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('same_ro')}
          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
            tab === 'same_ro' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Same RO ({sameRo.length})
        </button>
        <button
          onClick={() => setTab('cross_ro')}
          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
            tab === 'cross_ro' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Cross-RO ({crossRo.length})
        </button>
      </div>

      {items.length > 0 ? (
        <DataTable columns={columns} data={items} />
      ) : (
        <EmptyState text="No duplicate orders found" />
      )}
    </div>
  );
}
